import React, { useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiLogOut, FiX, FiLock, FiUnlock, FiStar } from 'react-icons/fi';
import './AdminLayout.css';
import { clearTokens } from '../../Api/authToken';
import { allVisibleItems, visibleNavGroups } from './adminNavModel';
import { loadSidebarOrder, mergeOrder, reorderList, saveSidebarOrder } from './adminUiPrefs';

const readDrag = (event) => {
  try {
    return JSON.parse(event.dataTransfer.getData('text/plain') || '{}');
  } catch {
    return {};
  }
};

const Sidebar = ({
  userRole,
  isOpen,
  onClose,
  basePath = '/admin',
  partnerOwnAccess = false,
  partnerChatAccess = false,
  partnerUserChatAccess = false,
  adminUserChatAccess = true,
  prefsScope = 'admin',
  pinned = true,
  onTogglePin,
}) => {
  const navigate = useNavigate();
  const isAdmin = userRole === 'admin' || userRole === 'sub_admin';
  const isPartner = userRole === 'partner';
  const customize = true;

  const [order, setOrder] = useState(() => loadSidebarOrder(prefsScope));
  const [dragOverId, setDragOverId] = useState('');

  const ctx = useMemo(
    () => ({ isAdmin, isPartner, partnerOwnAccess, partnerChatAccess, partnerUserChatAccess, adminUserChatAccess }),
    [isAdmin, isPartner, partnerOwnAccess, partnerChatAccess, partnerUserChatAccess, adminUserChatAccess]
  );

  const groups = useMemo(() => visibleNavGroups(ctx), [ctx]);
  const catalog = useMemo(() => {
    const map = new Map();
    allVisibleItems(ctx).forEach((item) => map.set(item.id, item));
    return map;
  }, [ctx]);

  const persist = (next) => {
    setOrder(next);
    saveSidebarOrder(next, prefsScope);
  };

  const orderedItems = (group) => {
    if (!customize) return group.items;
    const ids = mergeOrder(order.groups?.[group.id], group.items);
    return ids.map((id) => group.items.find((item) => item.id === id)).filter(Boolean);
  };

  const quickItems = useMemo(() => {
    if (!customize) return [];
    return mergeOrder(order.quickAccess, [...catalog.values()])
      .filter((id) => order.quickAccess.includes(id) && catalog.has(id))
      .map((id) => catalog.get(id));
  }, [customize, order.quickAccess, catalog]);

  const handleLogout = () => {
    if (isPartner) {
      clearTokens('partner');
      navigate('/partner/login');
    } else {
      clearTokens('admin');
      navigate('/admin/login');
    }
  };

  const closeIfNeeded = () => {
    if (!pinned) onClose?.();
  };

  const navLinkClass = ({ isActive }) => (isActive ? 'nav-item active' : 'nav-item');

  const toggleQuick = (itemId) => {
    const current = mergeOrder(order.quickAccess, [...catalog.values()])
      .filter((id) => order.quickAccess.includes(id));
    const nextIds = current.includes(itemId)
      ? current.filter((id) => id !== itemId)
      : [...current, itemId];
    persist({ ...order, quickAccess: nextIds });
  };

  const onDragStartItem = (event, itemId, groupId) => {
    event.dataTransfer.setData('text/plain', JSON.stringify({ itemId, groupId }));
    event.dataTransfer.effectAllowed = 'copyMove';
  };

  const allowDrop = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const dropOnGroupItem = (event, group, targetId) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverId('');
    const data = readDrag(event);
    if (!data.itemId || data.groupId !== group.id) return;
    const ids = mergeOrder(order.groups?.[group.id], group.items);
    persist({
      ...order,
      groups: {
        ...order.groups,
        [group.id]: reorderList(ids, data.itemId, targetId),
      },
    });
  };

  const dropOnQuick = (event, targetId) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverId('');
    const data = readDrag(event);
    if (!data.itemId || !catalog.has(data.itemId)) return;
    let ids = order.quickAccess.filter((id) => catalog.has(id));
    if (!ids.includes(data.itemId)) ids = [...ids, data.itemId];
    if (targetId && targetId !== data.itemId) ids = reorderList(ids, data.itemId, targetId);
    persist({ ...order, quickAccess: ids });
  };

  const renderItem = (item, { groupId, inQuick = false }) => {
    const Icon = item.icon;
    const selected = order.quickAccess.includes(item.id);
    const rowId = `${inQuick ? 'quick' : groupId}:${item.id}`;
    return (
      <div
        key={rowId}
        className={`nav-item-row${dragOverId === rowId ? ' is-drop-target' : ''}`}
        onDragOver={customize ? allowDrop : undefined}
        onDragEnter={customize ? () => setDragOverId(rowId) : undefined}
        onDragLeave={customize ? () => setDragOverId((cur) => (cur === rowId ? '' : cur)) : undefined}
        onDrop={customize
          ? (event) => (inQuick ? dropOnQuick(event, item.id) : dropOnGroupItem(event, { id: groupId, items: groups.find((g) => g.id === groupId)?.items || [] }, item.id))
          : undefined}
      >
        {customize && (
          <span
            className="nav-drag-handle"
            draggable
            title="Drag to reorder"
            onDragStart={(event) => onDragStartItem(event, item.id, inQuick ? 'quick-access' : groupId)}
          >
            ⋮⋮
          </span>
        )}
        <NavLink to={`${basePath}/${item.path}`} className={navLinkClass} onClick={closeIfNeeded}>
          <Icon className="nav-icon" />
          <span>{item.label}</span>
        </NavLink>
        {customize && !inQuick && (
          <button
            type="button"
            className={`nav-quick-star${selected ? ' is-on' : ''}`}
            title={selected ? 'Remove from Quick Access' : 'Add to Quick Access'}
            aria-pressed={selected}
            onClick={() => toggleQuick(item.id)}
          >
            <FiStar />
          </button>
        )}
        {customize && inQuick && (
          <button
            type="button"
            className="nav-quick-star is-on"
            title="Remove from Quick Access"
            onClick={() => toggleQuick(item.id)}
          >
            <FiX />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`admin-sidebar ${isOpen ? 'sidebar-open' : ''} ${pinned ? 'is-pinned' : ''}`}>
      <div className="sidebar-brand">
        <h2>{isPartner ? 'PIF Partner' : 'PIF Admin'}</h2>
        <div className="sidebar-brand-actions">
          {customize && (
            <button
              type="button"
              className={`sidebar-pin-btn${pinned ? ' is-on' : ''}`}
              onClick={onTogglePin}
              title={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
              aria-pressed={pinned}
            >
              {pinned ? <FiLock /> : <FiUnlock />}
              <span>{pinned ? 'Pinned' : 'Pin'}</span>
            </button>
          )}
          <button type="button" className="sidebar-close-btn" onClick={onClose}><FiX /></button>
        </div>
      </div>

      <nav className="sidebar-nav">
        {customize && (
          <div
            className={`nav-group quick-access-group${dragOverId === 'quick-zone' ? ' is-drop-target' : ''}`}
            onDragOver={allowDrop}
            onDragEnter={() => setDragOverId('quick-zone')}
            onDragLeave={() => setDragOverId((cur) => (cur === 'quick-zone' ? '' : cur))}
            onDrop={(event) => dropOnQuick(event)}
          >
            <p className="nav-group-title">Quick Access</p>
            {quickItems.length === 0 ? (
              <p className="quick-access-empty">Star a menu item or drop it here. Drag to reorder. The order is saved.</p>
            ) : (
              quickItems.map((item) => renderItem(item, { groupId: 'quick-access', inQuick: true }))
            )}
          </div>
        )}

        {groups.map((group) => (
          <div className="nav-group" key={group.id}>
            <p className="nav-group-title">{group.title}</p>
            {orderedItems(group).map((item) => renderItem(item, { groupId: group.id }))}
          </div>
        ))}

        <div className="nav-group mt-auto">
          <button type="button" className="nav-item logout-btn" onClick={handleLogout}>
            <FiLogOut className="nav-icon" />
            <span>Logout</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
